import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AdminClaimService } from '@core/services/admin-claim.service';
import { Store } from '@ngxs/store';
import { SetPlatformAdmin } from '@states/auth/auth.actions';
import { AuthSelectors } from '@states/auth/auth.selectors';

/**
 * Admin guard - protects admin-only routes
 * - Redirects unauthenticated users to sign-in
 * - Redirects users without the `admin: true` ID-token claim to home
 *
 * The claim is what firestore.rules and the admin callables check, so the
 * guard must agree with it. The profile `role` field is a display mirror; it
 * only decides whether a forced token refresh is worth trying (the claim may
 * have been granted since the cached token was minted).
 */
export const adminGuard: CanActivateFn = async (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const claims = inject(AdminClaimService);

  const user = store.selectSnapshot(AuthSelectors.getUser);

  // Not authenticated - redirect to sign-in
  if (!user) {
    return router.createUrlTree(['/sign-in'], {
      queryParams: { returnUrl: state.url },
    });
  }

  let isAdmin = await claims.hasAdminClaim();
  if (!isAdmin && user.role === 'admin') {
    isAdmin = await claims.hasAdminClaim(true);
    if (isAdmin) {
      // Keep AuthSelectors.isAdmin in step with the refreshed token
      store.dispatch(new SetPlatformAdmin(true));
    }
  }

  if (!isAdmin) {
    console.warn('Access denied: User is not an admin', {
      userId: user.id,
      role: user.role,
    });
    return router.createUrlTree(['/']);
  }

  return true;
};
